/**
 * Pure address-field mapper for the invoice/quote generator's bill-to / ship-to
 * auto-fill, extracted from `generate-invoice-form.tsx`'s `handleCustomerSelect`
 * (which inlined the same six-field block six times — three bill-to sources +
 * three ship-to copies).
 *
 * Deliberately reseller-wall-neutral: the CALLER picks the source (reseller,
 * dealer, or customer) inside the form's reseller gate and passes an already-
 * normalized {@link AddressSource}, so no `.reseller.name` access lives here —
 * the gate stays in the orchestrator (`useResellerAccess`). This module only
 * shapes the prefixed form fields and applies the form's empty / `U.S.A`
 * defaults.
 */
export interface AddressSource {
  company?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
}

export type AddressPrefix = 'bill_to' | 'ship_to';

/**
 * Map a normalized address source to the six `<prefix>_*` form fields, with the
 * form's defaults: empty string for a missing line, `U.S.A` for a missing
 * country (matching the form's initial state).
 */
export function addressFields(prefix: AddressPrefix, source: AddressSource): Record<string, string> {
  return {
    [`${prefix}_company`]: source.company || '',
    [`${prefix}_address`]: source.address || '',
    [`${prefix}_city`]: source.city || '',
    [`${prefix}_state`]: source.state || '',
    [`${prefix}_zip`]: source.zip || '',
    [`${prefix}_country`]: source.country || 'U.S.A',
  };
}
